import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Video, Calendar, Clock, User, ExternalLink, Youtube, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentLiveClasses = () => {
  const navigate = useNavigate();
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchLiveClasses();
  }, []);

  const fetchLiveClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-classes`);
      const data = await response.json();
      setLiveClasses(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-4">
            Live Classes
          </h1>
          <p className="text-xl text-gray-600 text-center">
            Join live sessions and interactive classes
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
            </div>
          ) : liveClasses.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Video className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No live classes scheduled</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveClasses.map((liveClass) => (
                <Card key={liveClass.id} className="hover:shadow-xl transition-shadow">
                  <CardHeader className={`${
                    liveClass.class_type === 'youtube_live' ? 'bg-red-50' : 'bg-red-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      {liveClass.class_type === 'youtube_live' ? (
                        <Youtube className="text-red-600" size={24} />
                      ) : (
                        <Video className="text-red-600" size={24} />
                      )}
                      <span className="px-2 py-1 bg-red-600 text-white rounded-full text-xs">
                        {liveClass.course_category}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{liveClass.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-3">
                    <p className="text-sm text-gray-600">{liveClass.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-700">
                        <Calendar className="mr-2 text-red-600" size={16} />
                        <span>{liveClass.scheduled_date}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Clock className="mr-2 text-red-600" size={16} />
                        <span>{liveClass.scheduled_time} ({liveClass.duration_minutes} mins)</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <User className="mr-2 text-red-600" size={16} />
                        <span>{liveClass.instructor_name}</span>
                      </div>
                    </div>

                    <a
                      href={liveClass.class_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-4"
                    >
                      <Button className={`w-full ${
                        liveClass.class_type === 'youtube_live' 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-red-600 hover:bg-red-700'
                      }`}>
                        {liveClass.class_type === 'youtube_live' ? (
                          <>
                            <Youtube className="mr-2" size={16} />
                            Watch on YouTube
                          </>
                        ) : (
                          <>
                            <Video className="mr-2" size={16} />
                            Join Class
                          </>
                        )}
                        <ExternalLink className="ml-2" size={14} />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudentLiveClasses;
